import React, { useState, useEffect } from "react";

const Welcome = () => {
  const [userData, setUserData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [prevUser, setPrevUser] = useState(null);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_ADMIN + "/qrCode/latestCheckinUser");
        const data = await response.json();
        const adjustedTime = new Date(data.latestCheckIn);
        adjustedTime.setHours(adjustedTime.getHours());
        adjustedTime.setMinutes(adjustedTime.getMinutes());
        data.latestCheckIn = adjustedTime;

        // Trigger re-animation when user changes
        if (!prevUser || prevUser.Firstname !== data.Firstname || prevUser.Lastname !== data.Lastname) {
          setVisible(false);
          setTimeout(() => {
            setUserData(data);
            setPrevUser(data);
            setAnimKey(k => k + 1);
            setVisible(true);
          }, 300);
        } else {
          setUserData(data);
          setVisible(true);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const pollData = setInterval(fetchData, 5000);
    fetchData();
    return () => clearInterval(pollData);
  }, [prevUser]);

  const firstName = userData?.Firstname || "";
  const lastName = userData?.Lastname || "";
  const timeStr = userData?.latestCheckIn?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <style>{styles}</style>
      <div className="wc-root">
        {/* Ambient orbs */}
        <div className="wc-orb wc-orb1" />
        <div className="wc-orb wc-orb2" />
        <div className="wc-orb wc-orb3" />

        {/* Dot grid */}
        <div className="wc-grid" />

        {/* Ripple rings */}
        <div className="wc-ring wc-ring1" />
        <div className="wc-ring wc-ring2" />
        <div className="wc-ring wc-ring3" />

        {/* Scan line */}
        <div className="wc-scanline" />

        <div className={`wc-center ${visible ? "wc-center--visible" : ""}`} key={animKey}>
          {userData ? (
            <>
              {/* Welcome label */}
              <div className="wc-label">
                <span className="wc-label-line" />
                <span className="wc-label-text">CHECKED IN</span>
                <span className="wc-label-line" />
              </div>

              {/* Name with glitch */}
              <div className="wc-name-wrap">
                <h1
                  className="wc-name"
                  data-text={firstName}
                >
                  {firstName}
                </h1>
                <h1
                  className="wc-name wc-name--last"
                  data-text={lastName}
                >
                  {lastName}
                </h1>
              </div>

              {/* Divider */}
              <div className="wc-divider" />

              {/* Time badge */}
              <div className="wc-time-wrap">
                <svg className="wc-time-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="wc-time-label">Last check-in at</span>
                <span className="wc-time">{timeStr}</span>
              </div>

              {/* Welcome message */}
              <p className="wc-sub">Welcome back. Have a great session.</p>
            </>
          ) : (
            <div className="wc-loading">
              <div className="wc-loader" />
              <p className="wc-loading-text">Awaiting check-in…</p>
            </div>
          )}
        </div>

        {/* Corner decorations */}
        <div className="wc-corner wc-corner-tl" />
        <div className="wc-corner wc-corner-tr" />
        <div className="wc-corner wc-corner-bl" />
        <div className="wc-corner wc-corner-br" />
      </div>
    </>
  );
};

export default Welcome;

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;700&display=swap');

  .wc-root {
    position: fixed;
    inset: 0;
    background: linear-gradient(160deg, #0a0a0f 0%, #12121a 40%, #0d0d15 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  }

  /* ── Dot grid ── */
  .wc-grid {
    position: fixed;
    inset: 0;
    background-image: radial-gradient(rgba(232,27,35,0.1) 1px, transparent 1px);
    background-size: 36px 36px;
    pointer-events: none;
    z-index: 0;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
    -webkit-mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
  }

  /* ── Scan line ── */
  .wc-scanline {
    position: fixed;
    top: -100%;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(to bottom, transparent, rgba(232,27,35,0.03), transparent);
    pointer-events: none;
    z-index: 1;
    animation: scanMove 8s linear infinite;
  }

  /* ── Orbs ── */
  .wc-orb {
    position: fixed;
    border-radius: 50%;
    pointer-events: none;
    z-index: 0;
  }
  .wc-orb1 {
    top: -15%; left: -10%;
    width: 700px; height: 700px;
    background: radial-gradient(circle, rgba(232,27,35,0.18) 0%, rgba(232,27,35,0.04) 50%, transparent 70%);
    filter: blur(8px);
    animation: orbFloat1 14s ease-in-out infinite;
  }
  .wc-orb2 {
    bottom: -20%; right: -10%;
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(180,10,18,0.14) 0%, rgba(120,0,10,0.04) 50%, transparent 70%);
    filter: blur(12px);
    animation: orbFloat2 18s ease-in-out infinite;
  }
  .wc-orb3 {
    top: 35%; left: 25%;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(255,80,80,0.06) 0%, transparent 70%);
    filter: blur(20px);
    animation: orbFloat3 22s ease-in-out infinite;
  }

  /* ── Rings ── */
  .wc-ring {
    position: fixed;
    border-radius: 50%;
    border: 1px solid rgba(232,27,35,0.12);
    pointer-events: none;
    z-index: 0;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
  }
  .wc-ring1 { width: 400px; height: 400px; animation: ringPulse 5s ease-out infinite; }
  .wc-ring2 { width: 650px; height: 650px; animation: ringPulse 5s ease-out infinite 1.6s; }
  .wc-ring3 { width: 900px; height: 900px; animation: ringPulse 5s ease-out infinite 3.2s; }

  /* ── Corner brackets ── */
  .wc-corner {
    position: fixed;
    width: 40px;
    height: 40px;
    pointer-events: none;
    z-index: 2;
    opacity: 0.4;
  }
  .wc-corner-tl { top: 24px; left: 24px; border-top: 1.5px solid #e81b23; border-left: 1.5px solid #e81b23; }
  .wc-corner-tr { top: 24px; right: 24px; border-top: 1.5px solid #e81b23; border-right: 1.5px solid #e81b23; }
  .wc-corner-bl { bottom: 24px; left: 24px; border-bottom: 1.5px solid #e81b23; border-left: 1.5px solid #e81b23; }
  .wc-corner-br { bottom: 24px; right: 24px; border-bottom: 1.5px solid #e81b23; border-right: 1.5px solid #e81b23; }

  /* ── Center content ── */
  .wc-center {
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .wc-center--visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── Label ── */
  .wc-label {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }
  .wc-label-line {
    display: block;
    width: 40px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(232,27,35,0.6));
  }
  .wc-label-line:last-child {
    background: linear-gradient(90deg, rgba(232,27,35,0.6), transparent);
  }
  .wc-label-text {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.3em;
    color: #e81b23;
    text-shadow: 0 0 12px rgba(232,27,35,0.6);
  }

  /* ── Name with glitch ── */
  .wc-name-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 0.92;
    margin-bottom: 6px;
  }
  .wc-name {
    position: relative;
    display: block;
    font-family: 'Bebas Neue', 'DM Sans', sans-serif;
    font-size: clamp(64px, 13vw, 140px);
    font-weight: 400;
    letter-spacing: 0.06em;
    color: #f4f4f8;
    text-shadow:
      0 0 30px rgba(232,27,35,0.2),
      0 2px 40px rgba(0,0,0,0.5);
    user-select: none;
    animation: nameReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) both, nameGlitch 8s infinite 2s;
  }
  .wc-name--last {
    color: transparent;
    -webkit-text-stroke: 1.5px rgba(232,27,35,0.7);
    text-shadow:
      0 0 40px rgba(232,27,35,0.25),
      0 0 80px rgba(232,27,35,0.1);
    animation: nameReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both, nameGlitch 8s infinite 3s;
  }

  /* Glitch pseudo layers */
  .wc-name::before,
  .wc-name::after {
    content: attr(data-text);
    position: absolute;
    top: 0; left: 0;
    width: 100%;
    font-size: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
    font-family: inherit;
    color: transparent;
    pointer-events: none;
  }
  .wc-name::before {
    -webkit-text-stroke: 1.5px rgba(232,27,35,0.5);
    animation: glitchBefore 8s infinite;
    clip-path: polygon(0 25%, 100% 25%, 100% 50%, 0 50%);
  }
  .wc-name::after {
    -webkit-text-stroke: 1.5px rgba(100,200,255,0.2);
    animation: glitchAfter 8s infinite;
    clip-path: polygon(0 65%, 100% 65%, 100% 85%, 0 85%);
  }

  /* ── Divider ── */
  .wc-divider {
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #e81b23, transparent);
    border-radius: 2px;
    box-shadow: 0 0 14px rgba(232,27,35,0.7);
    margin: 20px 0 22px;
    animation: dividerGlow 3s ease-in-out infinite;
  }

  /* ── Time ── */
  .wc-time-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(232,27,35,0.08);
    border: 1px solid rgba(232,27,35,0.2);
    border-radius: 100px;
    padding: 8px 20px;
    margin-bottom: 18px;
    backdrop-filter: blur(8px);
    animation: timePop 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both;
  }
  .wc-time-icon {
    color: rgba(232,27,35,0.8);
    flex-shrink: 0;
  }
  .wc-time-label {
    color: rgba(180,180,200,0.5);
    font-size: 0.78rem;
    letter-spacing: 0.05em;
  }
  .wc-time {
    color: #f0f0f5;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-shadow: 0 0 10px rgba(232,27,35,0.4);
  }

  /* ── Sub ── */
  .wc-sub {
    color: rgba(180,180,200,0.35);
    font-size: 0.82rem;
    letter-spacing: 0.12em;
    text-align: center;
    text-transform: uppercase;
    animation: fadeUp 0.6s ease 0.45s both;
  }

  /* ── Loading ── */
  .wc-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
  }
  .wc-loader {
    width: 48px;
    height: 48px;
    border: 2px solid rgba(232,27,35,0.15);
    border-top-color: #e81b23;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    box-shadow: 0 0 16px rgba(232,27,35,0.3);
  }
  .wc-loading-text {
    color: rgba(180,180,200,0.4);
    font-size: 0.8rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    animation: pulse 2s ease-in-out infinite;
  }

  /* ── Keyframes ── */
  @keyframes scanMove {
    0% { top: -100%; }
    100% { top: 200%; }
  }
  @keyframes orbFloat1 {
    0%,100% { transform: translate(0,0) scale(1); }
    33% { transform: translate(60px,-80px) scale(1.15); }
    66% { transform: translate(-40px,40px) scale(0.9); }
  }
  @keyframes orbFloat2 {
    0%,100% { transform: translate(0,0) scale(1); }
    33% { transform: translate(-70px,50px) scale(1.1); }
    66% { transform: translate(50px,-60px) scale(0.95); }
  }
  @keyframes orbFloat3 {
    0%,100% { transform: translate(0,0) scale(1); }
    50% { transform: translate(40px,60px) scale(1.2); }
  }
  @keyframes ringPulse {
    0%   { opacity: 0.5; transform: translate(-50%,-50%) scale(0.85); }
    100% { opacity: 0;   transform: translate(-50%,-50%) scale(1.15); }
  }
  @keyframes dividerGlow {
    0%,100% { opacity: 0.5; transform: scaleX(0.6); }
    50%      { opacity: 1;   transform: scaleX(1); }
  }
  @keyframes nameReveal {
    from { opacity: 0; transform: translateY(20px) skewX(-2deg); }
    to   { opacity: 1; transform: translateY(0) skewX(0); }
  }
  @keyframes nameGlitch {
    0%,88%,100% { transform: translate(0) skewX(0); }
    90% { transform: translate(-3px, 1px) skewX(-1deg); }
    92% { transform: translate(3px, -1px) skewX(1deg); }
    94% { transform: translate(-2px, 2px); }
    96% { transform: translate(2px, -1px); }
  }
  @keyframes glitchBefore {
    0%,88%,100% { transform: translate(0); opacity: 0; }
    90% { transform: translate(5px, 0); opacity: 1; }
    94% { transform: translate(-5px, 0); opacity: 1; }
    97% { transform: translate(0); opacity: 0; }
  }
  @keyframes glitchAfter {
    0%,88%,100% { transform: translate(0); opacity: 0; }
    91% { transform: translate(-5px, 0); opacity: 1; }
    95% { transform: translate(5px, 0); opacity: 1; }
    98% { transform: translate(0); opacity: 0; }
  }
  @keyframes timePop {
    from { opacity: 0; transform: scale(0.9); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%,100% { opacity: 0.4; }
    50%      { opacity: 0.8; }
  }

  @media (max-width: 480px) {
    .wc-ring1 { width: 280px; height: 280px; }
    .wc-ring2 { width: 430px; height: 430px; }
    .wc-ring3 { width: 580px; height: 580px; }
    .wc-corner { width: 28px; height: 28px; }
  }
`;